// src/components/decor/TerminalMessage.jsx
import { useEffect, useMemo, useState } from "react";
import "../../styles/terminal-message.css";

const TerminalMessage = ({
  lines = [
    "INITIALIZING SYSTEM...",
    "CONNECTING TO ARCHIVE...",
    "LOADING SYMBOLIC INDEX...",
    "READY // ENTER WHEN PREPARED",
  ],
  prompt = ">",
  typingSpeed = 40,
  deletingSpeed = 25,
  pauseBetween = 1200,
  startDelay = 500,
  cursorBlinkSpeed = 500,
}) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableLines = useMemo(() => lines, []);
  const [lineIndex, setLineIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [phase, setPhase] = useState("waiting"); // waiting | typing | pausing | deleting
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    let timeoutId;

    if (!stableLines.length) return;

    const currentLine = stableLines[lineIndex];

    if (phase === "waiting") {
      timeoutId = window.setTimeout(() => {
        setPhase("typing");
      }, startDelay);
    }

    if (phase === "typing") {
      if (displayedText.length < currentLine.length) {
        timeoutId = window.setTimeout(() => {
          setDisplayedText(currentLine.slice(0, displayedText.length + 1));
        }, typingSpeed);
      } else {
        timeoutId = window.setTimeout(() => {
          setPhase("pausing");
        }, pauseBetween);
      }
    }

    if (phase === "pausing") {
      timeoutId = window.setTimeout(() => {
        setPhase("deleting");
      }, pauseBetween);
    }

    if (phase === "deleting") {
      if (displayedText.length > 0) {
        timeoutId = window.setTimeout(() => {
          setDisplayedText(currentLine.slice(0, displayedText.length - 1));
        }, deletingSpeed);
      } else {
        setLineIndex((prev) => (prev + 1) % stableLines.length);
        setPhase("typing");
      }
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    displayedText,
    lineIndex,
    phase,
    stableLines,
    typingSpeed,
    deletingSpeed,
    pauseBetween,
    startDelay,
  ]);

  useEffect(() => {
    const cursorInterval = window.setInterval(() => {
      setShowCursor((prev) => !prev);
    }, cursorBlinkSpeed);

    return () => {
      window.clearInterval(cursorInterval);
    };
  }, [cursorBlinkSpeed]);

  return (
    <section className="terminal-message" aria-label="Terminal message">
      <div className="terminal-message__inner">
        <span className="terminal-message__prompt">{prompt}</span>
        <span className="terminal-message__text">{displayedText}</span>
        <span
          className={`terminal-message__cursor ${
            showCursor ? "terminal-message__cursor--visible" : ""
          }`}
        >
          █
        </span>
      </div>
    </section>
  );
};

export default TerminalMessage;